import { useMemo } from 'react';

function normalizeInteractionType(typeValue) {
  const raw = String(typeValue || '').trim().toLowerCase();
  if (!raw) return '';
  if (['move', 'guided_move', 'guidedmove', 'puzzle_move', 'puzzlemove'].includes(raw)) return 'move';
  if (['action', 'tap_square', 'tap', 'square'].includes(raw)) return 'action';
  if (['cct_check', 'cct', 'checklist'].includes(raw)) return 'cct_check';
  return raw;
}

function parseInteractionSafe(raw) {
  if (!raw) return null;
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const normalized = { ...parsed };
  const normalizedType = normalizeInteractionType(normalized.type);
  if (normalizedType) normalized.type = normalizedType;
  if (normalized.type === 'action' && !normalized.action) {
    const fallback = normalizeInteractionType(parsed.type);
    if (fallback && fallback !== 'action') normalized.action = fallback;
  }
  return normalized;
}

function parseMoveToken(rawValue) {
  if (!rawValue) return null;
  const token = String(rawValue)
    .trim()
    .toLowerCase()
    .replace(/[+#]/g, '')
    .replace('=', '')
    .replace(/\s+/g, '')
    .replace(/[→>-]/g, '');

  const match = token.match(/^([a-h][1-8])([a-h][1-8])([qrbn])?$/);
  if (!match) return null;
  return { from: match[1], to: match[2], promotion: match[3] || undefined };
}

function parseMoveObject(value) {
  if (!value) return null;
  if (typeof value === 'string') return parseMoveToken(value);
  if (typeof value !== 'object') return null;

  const from = String(value.from || '').trim().toLowerCase();
  const to = String(value.to || '').trim().toLowerCase();
  if (from.length === 2 && to.length === 2) {
    const rawPromotion = String(value.promotion || '').trim().toLowerCase();
    return {
      from,
      to,
      promotion: ['q', 'r', 'b', 'n'].includes(rawPromotion) ? rawPromotion : undefined
    };
  }

  return null;
}

function parseExpectedMove(interaction) {
  if (!interaction || typeof interaction !== 'object') return null;
  const expectedRaw =
    interaction.expected ??
    interaction.expected_move ??
    interaction.expectedMove ??
    (interaction.expected_from && interaction.expected_to
      ? {
          from: interaction.expected_from,
          to: interaction.expected_to,
          promotion: interaction.promotion
        }
      : null) ??
    (interaction.from && interaction.to
      ? {
          from: interaction.from,
          to: interaction.to,
          promotion: interaction.promotion
        }
      : null) ??
    null;
  return parseMoveObject(expectedRaw);
}

function parseLineUci(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => parseMoveObject(entry))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/\s+/)
      .map((entry) => parseMoveToken(entry))
      .filter(Boolean);
  }
  return [];
}

export function useChessStepEngine({ segment, legacyInteraction, enabled = false } = {}) {
  const v2Interaction = useMemo(() => {
    if (!enabled) return null;
    return parseInteractionSafe(segment?.interaction);
  }, [enabled, segment?.interaction]);

  const interaction = enabled ? v2Interaction : legacyInteraction;

  const guidedMoves = useMemo(() => {
    const source = interaction;
    if (!source) return [];

    const lineUciMoves = parseLineUci(source.lineUci);
    if (lineUciMoves.length) return lineUciMoves;

    const solutionMoves = parseLineUci(source.solutionMoves);
    if (solutionMoves.length) return solutionMoves;

    const guided = parseLineUci(source.guidedMoves);
    if (guided.length) return guided;

    const fallbackExpected = parseExpectedMove(source);
    return fallbackExpected ? [fallbackExpected] : [];
  }, [interaction]);

  const expectedMove = guidedMoves[0] || null;
  const hasExpectedMove = Boolean(expectedMove?.from) && Boolean(expectedMove?.to);

  const isMoveInteraction = useMemo(() => {
    if (!interaction) return false;
    if (interaction.type === 'move') return true;
    return guidedMoves.length > 0;
  }, [interaction, guidedMoves.length]);

  return {
    interaction,
    guidedMoves,
    expectedMove,
    hasExpectedMove,
    isMoveInteraction
  };
}
